import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';

export default function LocationForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: '',
        address: '',
        is_primary: false,
        phone: '',
        email: ''
    });

    const [loading, setLoading] = useState(isEdit);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() => {
        if (isEdit) {
            fetchLocation();
        }
    }, [id]);

    const fetchLocation = async () => {
        try {
            const response = await api.get(`/locations/${id}`);
            if (response.success) {
                setFormData(response.data);
            }
        } catch (error: any) {
            console.error('Error fetching location:', error);
            const msg = 'Failed to load location details';
            toast.error(msg);
            setErrors([msg]);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors([]);

        try {
            const response = isEdit
                ? await api.put(`/locations/${id}`, { location: formData })
                : await api.post('/locations', { location: formData });

            if (response.success) {
                toast.success(isEdit ? 'Warehouse updated successfully' : 'New warehouse established');
                navigate('/locations');
            } else {
                const backendErrors = response.errors?.length > 0
                    ? response.errors
                    : [response.message || response.error || 'Failed to save warehouse'];
                
                setErrors(backendErrors);
                toast.error(backendErrors[0]);
            }
        } catch (error: any) {
            console.error('Error saving location:', error);
            const msg = error.message || 'Something went wrong while saving';
            setErrors([msg]);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 mb-8 text-sm font-bold uppercase tracking-widest">
                <Link to="/locations" className="text-slate-400 hover:text-indigo-600 transition-colors">Logistics</Link>
                <span className="text-slate-300">/</span>
                <span className="text-slate-900">{isEdit ? 'Edit Warehouse' : 'New Warehouse'}</span>
            </div>

            <div className="bg-white rounded-[40px] border border-slate-100 shadow-2xl shadow-slate-200/50 overflow-hidden">
                <div className="p-8 md:p-12">
                    <div className="flex items-center gap-4 mb-10">
                        <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center">
                            <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                {isEdit ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                )}
                            </svg>
                        </div>
                        <div>
                            <h1 className="text-3xl font-black text-slate-900 tracking-tight">
                                {isEdit ? 'Refine Hub' : 'Establish Hub'}
                            </h1>
                            <p className="text-slate-500 font-medium">Configure your inventory storage point</p>
                        </div>
                    </div>

                    {errors.length > 0 && (
                        <div className="mb-10 p-5 bg-rose-50 border border-rose-100 rounded-3xl">
                            <div className="flex gap-3">
                                <svg className="w-5 h-5 text-rose-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <ul className="text-rose-700 text-sm font-bold space-y-1">
                                    {errors.map((err, i) => <li key={i}>{err}</li>)}
                                </ul>
                            </div>
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Core Identity */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Warehouse Name</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="e.g. Main Distribution Center"
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300"
                                />
                            </div>

                            <div className="space-y-2">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Primary Status</label>
                                <label className="flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 rounded-2xl cursor-pointer hover:bg-slate-100 transition-all">
                                    <input
                                        type="checkbox"
                                        checked={formData.is_primary}
                                        onChange={(e) => setFormData(prev => ({ ...prev, is_primary: e.target.checked }))}
                                        className="w-6 h-6 rounded-lg border-slate-300 text-indigo-600 focus:ring-indigo-500 transition-all"
                                    />
                                    <span className="font-bold text-slate-700">Set as Default Location</span>
                                </label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="space-y-2">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Phone</label>
                                <input
                                    type="tel"
                                    value={formData.phone}
                                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="+91..."
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Email</label>
                                <input
                                    type="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                    placeholder="operations@example.com"
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300"
                                />
                            </div>
                        </div>

                        {/* Location Details */}
                        <div className="space-y-2">
                            <label className="block text-sm font-black text-slate-700 uppercase tracking-wider">Physical Address</label>
                            <textarea
                                rows={3}
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                placeholder="Street, City, State, ZIP..."
                                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-bold text-slate-700 placeholder:text-slate-300 resize-none"
                            />
                        </div>


                        {/* Actions */}
                        <div className="flex flex-col md:flex-row gap-4 pt-10 border-t border-slate-50">
                            <button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 px-8 py-5 bg-indigo-600 text-white rounded-[24px] font-black text-lg hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] active:scale-[0.98]"
                            >
                                {submitting ? (
                                    <span className="flex items-center justify-center gap-3">
                                        <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Synchronizing Hub...
                                    </span>
                                ) : (
                                    isEdit ? 'Update Warehouse' : 'Initialize Warehouse'
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate('/locations')}
                                className="px-10 py-5 bg-slate-100 text-slate-600 rounded-[24px] font-black hover:bg-slate-200 transition-all"
                            >
                                Back to Hubs
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}
